import { getAuthHeaders } from '@/hooks/useAuth';
import { getVisitorId } from '@/lib/marketing';

const headers = () => ({ ...getAuthHeaders(), 'Content-Type': 'application/json' });

export interface CreatorPage {
  _id?: string;
  userId?: string;
  slug: string;
  displayName: string;
  bio: string;
  imageUrl: string;
  bannerImage?: string;
  isReader?: boolean;
  readerId?: string;
  ownCommunityRef?: string;   // attribution code; sales via a link carrying ?ref=<this> get the lower own-community commission
}

export type MuxIngestStatus = 'preparing' | 'ready' | 'errored' | 'deleted';

export interface ProductVideo {
  title: string;
  s3Key: string;
  duration?: number;
  thumbnailUrl?: string;
  freePreview?: boolean; // if true, this video can be watched without purchasing
  url?: string; // presigned URL, only returned from access endpoint
  episodeNumber?: number; // series episode order (1-based)
  logline?: string;       // optional one-line episode description
  dripDay?: number;       // release N days after access begins (0 = immediate)
  locked?: boolean;
  availableDate?: string; // absolute release date (YYYY-MM-DD); overrides dripDay
  orientation?: 'portrait' | 'landscape'; // derived from Mux aspect_ratio at ingest
  aspectRatio?: string;   // Mux aspect_ratio string, e.g. "9:16"
  fileType?: 'video' | 'pdf' | 'image' | 'file';
  fileSize?: number;
  // Mux marketplace fields — populated by /api/mux/ingest after save when
  // MUX_MARKETPLACE_ENABLED is on. Player still falls back to S3 until ready.
  muxAssetId?: string;
  muxPlaybackId?: string;
  muxIngestStatus?: MuxIngestStatus;
  muxIngestError?: string;
}

// A product renders vertical-first when the majority of its episodes are portrait.
// Orientation is populated at Mux ingest; unknown-orientation videos don't count.
export function isPortraitProduct(videos?: ProductVideo[]): boolean {
  const vids = videos || [];
  const portrait = vids.filter(v => v.orientation === 'portrait').length;
  const landscape = vids.filter(v => v.orientation === 'landscape').length;
  return portrait > 0 && portrait >= landscape;
}

export interface MuxPreview {
  videoIndex: number;
  sourceS3Key: string;
  muxAssetId: string;
  muxPlaybackId: string;
  muxIngestStatus: MuxIngestStatus;
  muxIngestError?: string;
}

// Questions a creator asks at registration (same field model as casting's
// custom questions builder, minus uploads). Answers are keyed by question id.
export type RegistrationQuestionType = 'text' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'yesno';
export interface RegistrationQuestion {
  id: string;
  label: string;
  type: RegistrationQuestionType;
  options?: string[];
  required?: boolean;
}
export type RegistrationAnswers = Record<string, string | string[]>;

export async function submitRegistrationAnswers(productId: string, answers: RegistrationAnswers): Promise<{ success: boolean; answers: RegistrationAnswers }> {
  const res = await fetch(`/api/creator/products/${productId}/answers`, { method: 'POST', headers: headers(), body: JSON.stringify({ answers }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not save your answers');
  return data;
}

export interface ReplayChapter {
  title: string;
  summary?: string;
  startSec: number;
  endSec: number;
}
export interface ReplayChapters {
  overview?: string;
  chapters: ReplayChapter[];
  videoIndex: number;
  source?: 'generated' | 'edited';
  generatedAt?: string;
}

export const formatClock = (sec: number): string => {
  const s = Math.max(0, Math.floor(sec || 0));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  return h ? `${h}:${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}` : `${m}:${String(r).padStart(2, '0')}`;
};

// Regenerate a replay's chapters from its recording (creator/admin).
export async function generateReplayChapters(productId: string): Promise<{ status: string; chapters: ReplayChapters | null; message: string | null }> {
  const res = await fetch(`/api/creator/products/${productId}/chapters/generate`, { method: 'POST', headers: headers() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || data.error || 'Could not generate chapters');
  return data;
}

export async function saveReplayChapters(productId: string, body: { overview?: string; chapters: Pick<ReplayChapter, 'title' | 'summary' | 'startSec'>[] }): Promise<ReplayChapters> {
  const res = await fetch(`/api/creator/products/${productId}/chapters`, { method: 'PUT', headers: headers(), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not save chapters');
  return data.chapters;
}

// --- Course Builder Types ---

// Chapter files (PDFs / images) that belong to a video lesson rather than to the
// top-level curriculum list. Titles/keys/URLs are withheld from public payloads —
// the course page sees only `attachmentCount`.
export interface LessonAttachment {
  id: string;
  title: string;
  s3Key: string;
  fileType: 'pdf' | 'image';
  url?: string; // presigned download URL, only returned from the access endpoint
}

export interface CourseLesson {
  id: string;
  title: string;
  description?: string;
  s3Key: string;
  fileType: 'video' | 'pdf' | 'image' | 'file';
  fileSize?: number;
  duration?: number;
  thumbnailUrl?: string;
  freePreview: boolean;
  dripDay: number;
  order: number;
  url?: string; // presigned URL from access endpoint
  locked?: boolean;
  availableDate?: string;
  attachments?: LessonAttachment[]; // entitled payloads carry presigned url per file
  attachmentCount?: number;         // public course page: count only, details withheld
  // Mux marketplace fields — mirrored from the flat product.videos[] entry
  // at the same s3Key. The editor uses these to render status badges; the
  // viewer reads them off product.videos[] directly.
  muxAssetId?: string;
  muxPlaybackId?: string;
  muxIngestStatus?: MuxIngestStatus;
  muxIngestError?: string;
}

export interface CourseModule {
  id: string;
  title: string;
  description?: string;
  order: number;
  lessons: CourseLesson[];
}

// --- Product File Upload ---

export async function getProductFileUploadUrl(fileName: string, fileType: string): Promise<{ uploadUrl: string; key: string; fileType: string }> {
  const res = await fetch('/api/creator/products/upload-url', {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ fileName, fileType }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to get upload URL'); }
  return res.json();
}

/** A file that comes with the product (slides, a worksheet). `url` is a signed
 *  link and only arrives on entitled payloads (/join, /access). */
export interface ProductDownload { id: string; title: string; s3Key?: string; bytes?: number | null; url?: string }

export interface CreatorProduct {
  /** Files that come with registering or buying: titles public, links for registrants and buyers. */
  downloads?: ProductDownload[];
  /** What this studio's members pay (founding Studio partners only); unset = no studio price. */
  studioMemberPriceCents?: number;
  /** 'stream' (default): presenters in the meeting, everyone else watches the stream. 'meeting': everyone joins the meeting; casting tunes the room for scale and it holds 150. */
  audienceMode?: 'stream' | 'meeting';
  /** Post-event follow-ups: opt-in per event, two editable emails ({name}, {title}, {next}). */
  followupEmails?: { enabled: boolean; attended?: { subject: string; body: string } | null; missed?: { subject: string; body: string } | null } | null;
  /** Active replay: for `days` after the last session the host answers replay questions within 24 hours. */
  activeReplay?: { enabled: boolean; days: number } | null;
  _id: string;
  creatorId?: string;
  slug: string;
  title: string;
  description: string;
  price: number; // cents
  productType: 'single' | 'series' | 'webinar' | 'bundle' | 'goods';
  /** productType 'goods': a physical thing (ships) or a digital download. */
  goods?: GoodsConfig;
  format?: 'single' | 'series'; // 'series' = multi-episode video product (productType stays 'single')
  /** productType 'bundle': the stored config (edit payload) or, on store cards, a summary. */
  bundle?: BundleConfig | BundleCard;
  /** Cohort events: one registration covers every date (no session to pick). */
  attendAllDates?: boolean;
  /** Agency-only events. */
  restrictToAgencies?: { id: string; name?: string }[];
  category?: string | null; // curated marketplace subject slug (see /api/marketplace/taxonomy)
  tags?: string[];
  plusOfferMonths?: number | null; // class-page bundle: N free months of Plus with purchase
  reviewStatus?: 'draft' | 'pending' | 'approved' | 'rejected'; // first live-event review gate
  reviewReason?: string; // admin note when a live event is rejected
  coverImage?: string; // S3 key for product cover/thumbnail
  videos: ProductVideo[];
  modules?: CourseModule[]; // structured course layout (series only)
  published: boolean;
  createdAt?: string;
  pricingMode?: 'one_time' | 'subscription';
  subscriptionInterval?: 'month';
  // Products granted with this one. On a subscription product they last as long
  // as the subscription; on a one-time product they are permanent.
  includedProductIds?: string[];
  memberCount?: number;
  muxPreview?: MuxPreview;
  // Featured-event replays: chapters generated from the recording's captions
  // (creator-editable). videoIndex says which entry in `videos` they belong to.
  replayChapters?: ReplayChapters | null;
  // Questions asked at registration / early access (creator-defined).
  registrationQuestions?: RegistrationQuestion[];
  // Interview-only classes: a request form replaces checkout (see InquiryForm).
  inquiry?: { enabled: boolean; buttonLabel?: string; requestType?: string } | null;
  // HJ+ member pricing (admin-set, public-safe fields only). Commission and the
  // provider notify email are stripped server-side and never reach the client.
  memberPriceCents?: number;
  memberAnnualLimit?: number;
  memberRateCollection?: string;
  memberCapUpsellUrl?: string;
  // Class-feed (e.g. ITA) event fields — set on externally-sourced dated classes.
  eventDate?: string;        // ISO — the NEXT upcoming occurrence (mirrors earliest future eventDates entry)
  eventDates?: string[];     // ISO — every scheduled occurrence (featured events can run on multiple dates)
  durationMins?: number | null; // featured events
  minAge?: number | null;    // set to gate registration behind an "I am N or older" attestation
  joinProvider?: string;        // featured events — display hint
  instructor?: string;
  hostRole?: string;
  // Presenter addresses. Internal: stripped from every public payload, returned
  // only on the creator's own edit fetch.
  hostEmails?: string[];
  // Who shares the creator payout, as percentages of it. Unassigned share stays
  // with the creator of record. Internal, like hostEmails.
  payouts?: { email: string; name?: string | null; percent: number }[];
  // Other stores that own this event with its creator. Public payloads carry
  // slug + name only; the builder also gets userId + email.
  coOwners?: { slug: string; displayName: string; userId?: string; email?: string | null }[];
  bookedCount?: number;       // past classes on a store: how many booked
  canSetPayouts?: boolean;    // builder only: creator of record or admin
  seatsRemaining?: number | null;
  classUrl?: string;         // external registration/detail link on the provider site
  providerKey?: string;      // e.g. 'ita' for In The Act classes
  galleryImages?: string[];  // marketing stills (S3 keys or absolute URLs) shown on the booking page
  testimonials?: { quote: string; name: string; title?: string }[]; // social proof shown on the booking page
  ticketTiers?: TicketTier[]; // live events: e.g. participant vs observer seats
}

// Ticket tier for live events (participant vs observer). seatsRemaining/soldOut
// are computed server-side on the public payload; null seatsRemaining = uncapped.
export interface TicketTier {
  /** Acquisition tier (bundled Plus months); members are sent to the plain tier. */
  nonMembersOnly?: boolean;
  /** Members-only tier (e.g. a handful of free seats); non-members are sent to the regular seat. */
  membersOnly?: boolean;
  key: string;
  label: string;
  description?: string;
  priceCents: number;
  memberPriceCents?: number;        // standing member price (monthly members)
  annualMemberPriceCents?: number;  // once-a-year benefit price for ANNUAL members (e.g. $0)
  plusOfferMonths?: number | null;  // free months of Hey Joe Plus bundled with THIS tier
  seatCap?: number;
  seatsRemaining?: number | null;
  soldOut?: boolean;
}

// A single tier's resolved deal for the signed-in buyer (member-status only).
export interface MemberStatusTier {
  key: string;
  label: string;
  regularPriceCents: number;
  chargeCents: number;          // what THIS buyer pays for this tier
  memberRate: boolean;
  annualFreeClass: boolean;     // this tier is their once-a-year free class
  overCap: boolean;
}

// Per-buyer member pricing status for a product (drives the member-price badge
// and the over-cap "join the provider" nudge).
export interface MemberStatus {
  isPlusMember: boolean;
  /** 'active' | 'trialing' | 'past_due' | null — why isPlusMember is what it is. */
  buyerStatus?: string | null;
  /** A campaign trial's included member-priced events, and how many are spent. */
  trialAllowance?: number;
  trialAllowanceUsed?: number;
  /** This buyer's own live registration, so the page can offer a way out of it. */
  myRegistration?: { purchaseId: string; amountPaid: number; eventDate: string | null } | null;
  /** Hours before the event inside which a refund stops being automatic. */
  cancellationHours?: number | null;
  buyerTier: string | null; // 'yearly' | 'monthly' | 'cd_plus' | null
  memberRate: boolean;      // gets the member deal on this purchase
  overCap: boolean;         // member, but used up the annual allowance
  chargeCents: number;      // what they'd actually be charged
  regularPriceCents: number;
  memberPriceCents: number | null;
  annualLimit: number | null;
  used: number | null;
  tiers: MemberStatusTier[] | null; // per-tier deals for tiered products (null otherwise)
  upsellUrl: string | null;
  /** Studio membership pricing (founding Studio partners): getting it now, and the price. */
  studioMemberRate?: boolean;
  studioMemberPriceCents?: number | null;
}

/** What this creator's account may offer (e.g. studio memberships that bundle products). */
export async function fetchCreatorCapabilities(): Promise<{ bundleMembership: boolean }> {
  const res = await fetch('/api/creator/capabilities', { headers: headers() });
  if (!res.ok) return { bundleMembership: false };
  return res.json();
}

export async function fetchMemberStatus(productId: string): Promise<MemberStatus> {
  const res = await fetch(`/api/creator/products/${productId}/member-status`, { headers: headers() });
  if (!res.ok) throw new Error('Failed to fetch member status');
  return res.json();
}

// Cancel your own registration. Refund rules live on the server; the caller only
// needs to know whether money came back and whether the cancellation window had
// already closed.
export async function cancelMyRegistration(purchaseId: string): Promise<{
  cancelled: boolean; refunded: boolean; refundCents: number; insideWindow: boolean; windowHours: number | null;
}> {
  const res = await fetch(`/api/purchases/${purchaseId}/cancel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not cancel your registration');
  return res.json();
}

export interface ProductPurchase {
  _id: string;
  productId: string;
  buyerUserId: string;
  amountPaid: number;
  purchasedAt: string;
  eventDate?: string;   // for featured events: the occurrence they registered for
  price?: number;
  tier?: string;
  product?: CreatorProduct;
}

// --- Creator Page ---

export async function fetchMyCreatorPage(): Promise<CreatorPage | null> {
  const res = await fetch('/api/creator/page', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch creator page');
  const data = await res.json();
  return data.page;
}

export async function updateCreatorPage(page: Partial<CreatorPage>): Promise<CreatorPage> {
  const res = await fetch('/api/creator/page', { method: 'PUT', headers: headers(), body: JSON.stringify(page) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to save'); }
  const data = await res.json();
  return data.page;
}

// A creator who is also a published reader: the store shows their one-on-one
// listing (reader, session director, coach rates) straight from the reader
// profile. Same shape the reader page uses; `_id` is the reader's user id.
export interface CreatorReaderListing {
  _id: string;
  first_name: string;
  last_name: string;
  freelancer_profile?: {
    reader_rate?: number;
    reader_director_rate?: number;
    reader_coach_rate?: number;
    reader_categories?: string[];
    reader_experience?: string;
    reader_availability_notes?: string;
    timezone?: string;
  };
}

// A short captioned 9:16 cut from one of the creator's replays (creator_clips).
// videoUrl/posterUrl come back presigned for a few hours.
export interface StoreClip {
  _id: string;
  creatorSlug: string;
  productId: string;
  title: string;
  videoUrl: string;
  posterUrl?: string | null;
  durationSec?: number;
  startSec?: number;
  endSec?: number;
  published?: boolean;
  order?: number;
  productTitle?: string;
}

export async function fetchPublicCreatorPage(slug: string): Promise<{ page: CreatorPage; products: CreatorProduct[]; pastProducts?: CreatorProduct[]; reader?: CreatorReaderListing | null; followerCount?: number; isFollowing?: boolean; clips?: StoreClip[] }> {
  // Forward dev-only ?preview=ita-preview so staged ITA classes show in the store (no-op on prod).
  const preview = new URLSearchParams(window.location.search).get('preview');
  // Auth headers so the store knows whether this viewer follows the creator.
  const res = await fetch(`/api/creators/${slug}${preview ? `?preview=${encodeURIComponent(preview)}` : ''}`, { headers: { ...getAuthHeaders() } });
  if (!res.ok) throw new Error('Creator not found');
  return res.json();
}

// Message the creator from their store. Signed-in only; the server emails the
// creator with the sender as reply-to and keeps a copy.
export async function sendCreatorMessage(slug: string, message: string): Promise<{ ok: true; creatorFirst: string }> {
  const res = await fetch(`/api/creators/${slug}/message`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    body: JSON.stringify({ message }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not send that note.');
  return data;
}

// Follow / unfollow a creator from their store. Signed-in only.
export async function setFollowingCreator(slug: string, follow: boolean): Promise<{ isFollowing: boolean; followerCount: number }> {
  const res = await fetch(`/api/creators/${slug}/follow`, {
    method: follow ? 'POST' : 'DELETE',
    headers: { ...getAuthHeaders() },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || (follow ? 'Could not follow just now.' : 'Could not unfollow just now.'));
  return data;
}

// Admin: every clip for a store, published or not.
export async function adminListStoreClips(slug: string): Promise<StoreClip[]> {
  const res = await fetch(`/api/admin/creator-clips?slug=${encodeURIComponent(slug)}`, { credentials: 'include', headers: { ...getAuthHeaders() } });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not load clips');
  return data.clips || [];
}

export async function adminUpdateStoreClip(id: string, patch: { published?: boolean; title?: string }): Promise<StoreClip> {
  const res = await fetch(`/api/admin/creator-clips/${id}`, { method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify(patch) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not update the clip');
  return data.clip;
}

export async function adminReorderStoreClips(slug: string, ids: string[]): Promise<void> {
  const res = await fetch('/api/admin/creator-clips/reorder', { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, body: JSON.stringify({ slug, ids }) });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Could not reorder'); }
}

export async function adminDeleteStoreClip(id: string): Promise<void> {
  const res = await fetch(`/api/admin/creator-clips/${id}`, { method: 'DELETE', credentials: 'include', headers: { ...getAuthHeaders() } });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Could not delete'); }
}

export interface PublicProductCreator {
  slug: string;
  displayName: string;
  imageUrl?: string;
  bio?: string;
}

// Public (logged-out) view of a single product/course — used by the shareable course page.
// viewerIsOwner: the logged-in viewer is the product's creator (or an admin) —
// the page offers them the full course view instead of purchase locks.
// The agency invite code from ?invite=, remembered for this product (localStorage,
// so a new account confirmed in another tab still carries it back).
export function agencyInviteCode(productId: string): string {
  const key = `hj_agency_invite_${productId}`;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('invite');
    if (fromUrl) { localStorage.setItem(key, fromUrl); return fromUrl; }
    return localStorage.getItem(key) || '';
  } catch { return ''; }
}

export async function fetchPublicProduct(productId: string): Promise<{ product: CreatorProduct; creator: PublicProductCreator | null; viewerIsOwner?: boolean; previewOnly?: boolean; access?: { restrictedTo: string; eligible: boolean | null; earlyAccess?: boolean; viaInvite?: boolean } | null; watchingDates?: boolean; autoEnroll?: string[]; bundle?: BundleSummary }> {
  // Forward dev-only ?preview=ita-preview so staged ITA class detail pages open before publish (no-op on prod).
  // Auth headers let the owner (or an admin) preview their own unpublished draft.
  const preview = new URLSearchParams(window.location.search).get('preview');
  // An agency's invite link (?invite=) lets its talent register before they're on
  // the roster. Kept per product so it survives the trip through sign-in.
  const invite = agencyInviteCode(productId);
  const qs = new URLSearchParams();
  if (preview) qs.set('preview', preview);
  if (invite) qs.set('invite', invite);
  const res = await fetch(`/api/creator/products/${productId}/public${qs.toString() ? `?${qs}` : ''}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Product not found');
  return res.json();
}

// --- AI listing classifier (shared chooser) ---

export interface ListingClassification {
  type: 'webinar' | 'course' | 'video' | 'series' | null;
  title?: string;
  description?: string;
  suggestedPriceCents?: number;
}

// Classify a free-text listing idea into a type + prefill. Never throws for the
// caller's happy path — a null/failed response just means "fall back to tiles."
export async function classifyListing(text: string): Promise<ListingClassification> {
  try {
    const res = await fetch('/api/creator/classify-listing', { method: 'POST', headers: headers(), body: JSON.stringify({ text }) });
    if (!res.ok) return { type: null };
    return await res.json();
  } catch {
    return { type: null };
  }
}

// --- Brand image generator (Image Studio + concierge) ---

export interface GenerateImageParams {
  output: 'banner' | 'card' | 'logo' | 'poster';
  layout?: 'text-left-cutout-right' | 'centered' | 'text-only';
  background?: 'cream' | 'yellow' | 'pink' | 'blue' | 'ink';
  eyebrow?: string;
  headline: string[];
  tagline?: string;
  logoKey?: string;
  cutoutKey?: string;
  logoIsLight?: boolean;
  monogram?: string;
}

// Render a store banner / class card server-side; returns the bare S3 key.
export async function generateBrandImage(params: GenerateImageParams): Promise<{ key: string }> {
  const res = await fetch('/api/creator/generate-image', { method: 'POST', headers: headers(), body: JSON.stringify(params) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to generate image'); }
  return res.json();
}

// Knock the background out of an uploaded photo (BiRefNet). Returns the bare S3
// key of the transparent cutout. Run once at upload; reuse the key for previews.
export async function knockoutPhoto(key: string): Promise<{ key: string }> {
  const res = await fetch('/api/creator/knockout', { method: 'POST', headers: headers(), body: JSON.stringify({ key }) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Background removal failed'); }
  return res.json();
}

// --- Course concierge (guided course builder) ---

export interface ConciergeOutline {
  title: string;
  description: string;
  modules: { id?: string; title: string; lessons: { id?: string; title: string; description: string }[] }[];
}

export interface ConciergeContext { audience?: string; outcomes?: string; angle?: string; depth?: 'short' | 'standard' | 'full'; }
export async function conciergeOutline(idea: string, context?: ConciergeContext): Promise<ConciergeOutline> {
  try {
    const res = await fetch('/api/creator/concierge/outline', { method: 'POST', headers: headers(), body: JSON.stringify({ idea, context }) });
    if (!res.ok) return { title: '', description: '', modules: [] };
    return await res.json();
  } catch { return { title: '', description: '', modules: [] }; }
}

export async function conciergeLesson(args: { courseTitle?: string; moduleTitle?: string; lessonTitle?: string; note?: string }): Promise<{ title: string; description: string }> {
  try {
    const res = await fetch('/api/creator/concierge/lesson', { method: 'POST', headers: headers(), body: JSON.stringify(args) });
    if (!res.ok) return { title: '', description: '' };
    return await res.json();
  } catch { return { title: '', description: '' }; }
}

export interface ConciergeChatMessage { role: 'user' | 'assistant'; content: string; }

export async function conciergeChat(messages: ConciergeChatMessage[], context: { courseTitle?: string; moduleTitle?: string; lessonTitle?: string; lessonDescription?: string }): Promise<{ reply: string }> {
  try {
    const res = await fetch('/api/creator/concierge/chat', { method: 'POST', headers: headers(), body: JSON.stringify({ messages, context }) });
    if (!res.ok) return { reply: '' };
    return await res.json();
  } catch { return { reply: '' }; }
}

// Conversational whole-curriculum builder. Returns a chat reply and optionally a
// complete revised outline (reusing existing module/lesson ids so recorded
// lessons survive). Fails soft to an empty reply + no outline.
export interface ConciergeCurriculum {
  title: string;
  description: string;
  modules: { id: string; title: string; lessons: { id: string; title: string; description: string; hasVideo: boolean }[] }[];
}

export async function conciergeCurriculumChat(messages: ConciergeChatMessage[], curriculum: ConciergeCurriculum): Promise<{ reply: string; outline: ConciergeOutline | null }> {
  try {
    const res = await fetch('/api/creator/concierge/curriculum-chat', { method: 'POST', headers: headers(), body: JSON.stringify({ messages, curriculum }) });
    if (!res.ok) return { reply: '', outline: null };
    return await res.json();
  } catch { return { reply: '', outline: null }; }
}

export async function conciergeScript(args: { courseTitle?: string; moduleTitle?: string; lessonTitle?: string; lessonDescription?: string; note?: string }): Promise<{ script: string }> {
  try {
    const res = await fetch('/api/creator/concierge/script', { method: 'POST', headers: headers(), body: JSON.stringify(args) });
    if (!res.ok) return { script: '' };
    return await res.json();
  } catch { return { script: '' }; }
}

// --- Series Studio (development workspace) ---

export type SeriesFormat = 'pov' | 'sketch' | 'mockumentary' | 'recurring-bit';

export interface SeriesCharacter { id?: string; name: string; description?: string; playedBy?: string; }
export interface SeriesLocation { id?: string; name: string; description?: string; notes?: string; }
export interface SeriesAsset { id?: string; label: string; kind: 'prop' | 'wardrobe' | 'other'; done: boolean; }
export interface SeriesShot { shot: string; framing?: string; note?: string; }
export type SeriesEpisodeStatus = 'planned' | 'scripted' | 'shot' | 'polished' | 'attached';
export interface SeriesEpisode {
  id?: string;
  number: number;
  title: string;
  logline?: string;
  beats?: string[];
  hookOptions?: string[];
  script?: string;
  shotList?: SeriesShot[];
  status: SeriesEpisodeStatus;
  s3Key?: string;
  // Footage refs (set once an episode is shot/attached).
  muxAssetId?: string;
  muxPlaybackId?: string;
  muxIngestStatus?: MuxIngestStatus;
  thumbnailUrl?: string;
  duration?: number;
}
export interface SeriesProject {
  _id?: string;
  title: string;
  premise?: string;
  format?: SeriesFormat | '';
  tone?: string;
  aspect?: '9:16';
  characters: SeriesCharacter[];
  locations: SeriesLocation[];
  runningBits: string[];
  assets: SeriesAsset[];
  episodes: SeriesEpisode[];
  productId?: string;
  createdAt?: string;
  updatedAt?: string;
}

const emptySeriesProject = (): SeriesProject => ({
  title: '', premise: '', format: '', tone: '', aspect: '9:16',
  characters: [], locations: [], runningBits: [], assets: [], episodes: [],
});

export async function fetchSeriesProjects(): Promise<SeriesProject[]> {
  const res = await fetch('/api/creator/series-projects', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch series projects');
  const data = await res.json();
  return data.projects || [];
}

export async function fetchSeriesProject(id: string): Promise<SeriesProject> {
  const res = await fetch(`/api/creator/series-projects/${id}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Series project not found');
  const data = await res.json();
  return data.project;
}

export async function createSeriesProject(project: Partial<SeriesProject>): Promise<SeriesProject> {
  const res = await fetch('/api/creator/series-projects', { method: 'POST', headers: headers(), body: JSON.stringify(project) });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Failed to create'); }
  const data = await res.json();
  return data.project;
}

export async function updateSeriesProject(id: string, project: Partial<SeriesProject>): Promise<SeriesProject> {
  const res = await fetch(`/api/creator/series-projects/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(project) });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Failed to save'); }
  const data = await res.json();
  return data.project;
}

export async function deleteSeriesProject(id: string): Promise<void> {
  const res = await fetch(`/api/creator/series-projects/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to delete');
}

// AI (structure-first, fail-soft to empty scaffolds — the UI proceeds manually).
export interface SeriesBibleDraft {
  titleOptions: string[]; premise: string; format: SeriesFormat | '';
  tone: string; characters: SeriesCharacter[]; locations: SeriesLocation[]; runningBits: string[];
}
export async function seriesDevelop(idea: string): Promise<SeriesBibleDraft> {
  const empty: SeriesBibleDraft = { titleOptions: [], premise: '', format: '', tone: '', characters: [], locations: [], runningBits: [] };
  try {
    const res = await fetch('/api/creator/series/develop', { method: 'POST', headers: headers(), body: JSON.stringify({ idea }) });
    if (!res.ok) return empty;
    return await res.json();
  } catch { return empty; }
}

export interface SeriesPlanEpisode { number: number; title: string; logline: string; beats: string[]; }
// A failed call used to come back indistinguishable from a season with no
// episodes in it, so the UI told people to "try again in a moment" when the
// request had actually died. The server now sends a code; pass it through.
export type SeriesFailure = { error?: string; code?: 'SERIES_TIMEOUT' | 'SERIES_UNAVAILABLE' };
export async function seriesPlan(bible: Partial<SeriesProject>, episodeCount?: number): Promise<{ episodes: SeriesPlanEpisode[] } & SeriesFailure> {
  try {
    const res = await fetch('/api/creator/series/plan', { method: 'POST', headers: headers(), body: JSON.stringify({ bible, episodeCount }) });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { episodes: [], error: body.error, code: body.code || 'SERIES_UNAVAILABLE' };
    return body;
  } catch { return { episodes: [], code: 'SERIES_UNAVAILABLE' }; }
}

export interface SeriesStructure { beats: string[]; hookOptions: string[]; shotList: SeriesShot[]; overlayGags: string[]; }
export async function seriesStructure(bible: Partial<SeriesProject>, episode: Partial<SeriesEpisode>): Promise<SeriesStructure & SeriesFailure> {
  const empty: SeriesStructure = { beats: [], hookOptions: [], shotList: [], overlayGags: [] };
  try {
    const res = await fetch('/api/creator/series/structure', { method: 'POST', headers: headers(), body: JSON.stringify({ bible, episode }) });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ...empty, error: body.error, code: body.code || 'SERIES_UNAVAILABLE' };
    return body;
  } catch { return { ...empty, code: 'SERIES_UNAVAILABLE' }; }
}

export async function seriesChat(messages: ConciergeChatMessage[], context: string): Promise<{ reply: string }> {
  try {
    const res = await fetch('/api/creator/series/chat', { method: 'POST', headers: headers(), body: JSON.stringify({ messages, context }) });
    if (!res.ok) return { reply: '' };
    return await res.json();
  } catch { return { reply: '' }; }
}

export async function seriesDraft(bible: Partial<SeriesProject>, episode: Partial<SeriesEpisode>, beats?: string[]): Promise<{ draft: string } & SeriesFailure> {
  try {
    const res = await fetch('/api/creator/series/draft', { method: 'POST', headers: headers(), body: JSON.stringify({ bible, episode, beats }) });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { draft: '', error: body.error, code: body.code || 'SERIES_UNAVAILABLE' };
    return body;
  } catch { return { draft: '', code: 'SERIES_UNAVAILABLE' }; }
}

// Copy a polished lesson clip into the permanent creator-products prefix.
export async function finalizeLessonVideo(url: string): Promise<{ key: string }> {
  const res = await fetch('/api/creator/finalize-video', { method: 'POST', headers: headers(), body: JSON.stringify({ url }) });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Finalize failed'); }
  return res.json();
}

// --- Bundles and season passes ---

export interface BundleConfig {
  mode: 'fixed' | 'pass';
  through?: string | null;
  tiers?: Record<string, string>;
}
/** What a store card gets for a bundle. */
export interface BundleCard {
  mode: 'fixed' | 'pass';
  through?: string | null;
  itemCount: number;
  saveCents: number;
  unavailable?: string | null;
}
export interface BundleItem {
  _id: string;
  title: string;
  productType: string;
  format?: string | null;
  coverImage?: string | null;
  eventDate?: string | null;
  sessions: string[];
  tierLabel?: string | null;
  priceCents: number;
}
/** What the bundle page and purchase page show. */
export interface BundleSummary {
  mode: 'fixed' | 'pass';
  through?: string | null;
  items: BundleItem[];
  separateCents: number;
  saveCents: number;
  unavailable?: string | null;
}
export interface BundleInput {
  title: string;
  description?: string;
  price: number; // cents
  coverImage?: string;
  mode: 'fixed' | 'pass';
  includedProductIds?: string[];
  tiers?: Record<string, string>;
  through?: string; // YYYY-MM-DD
  published?: boolean;
}

export async function saveBundle(input: BundleInput, id?: string): Promise<{ product: CreatorProduct; bundle: BundleSummary }> {
  const res = await fetch(id ? `/api/creator/bundles/${id}` : '/api/creator/bundles', {
    method: id ? 'PUT' : 'POST', headers: headers(), body: JSON.stringify(input),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not save the bundle');
  return data;
}

// --- Products ---

export async function fetchMyProducts(): Promise<CreatorProduct[]> {
  const res = await fetch('/api/creator/products', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch products');
  const data = await res.json();
  return data.products;
}

// Load one product for the builders. Authorized server-side by viewerOwnsProduct,
// so admins can open any product — fetchMyProducts() only ever returns your own,
// which is why editing someone else's used to 'not found'.
export async function fetchProductForEdit(productId: string): Promise<CreatorProduct> {
  const res = await fetch(`/api/creator/products/${productId}/edit`, { headers: getAuthHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to load product');
  }
  const data = await res.json();
  return data.product;
}

export async function createProduct(product: Partial<CreatorProduct>): Promise<CreatorProduct> {
  const res = await fetch('/api/creator/products', { method: 'POST', headers: headers(), body: JSON.stringify(product) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to create'); }
  const data = await res.json();
  return data.product;
}

export async function updateProduct(id: string, product: Partial<CreatorProduct>): Promise<CreatorProduct> {
  const res = await fetch(`/api/creator/products/${id}`, { method: 'PUT', headers: headers(), body: JSON.stringify(product) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to update'); }
  const data = await res.json();
  return data.product;
}

export async function deleteProduct(id: string): Promise<void> {
  const res = await fetch(`/api/creator/products/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to delete');
}

// --- Purchases ---

// Guest checkout: email first. A Plus member's email comes back { member: true }
// (sign in for the member price); a free or agency-only event comes back
// { requiresSignIn: true }; otherwise { checkoutUrl } to Stripe at the public price.
export async function guestCheckout(productId: string, email: string, tier?: string, discountCode?: string, eventDate?: string, registerToken?: string, extra?: { invite?: string; firstName?: string; lastName?: string; answers?: Record<string, string | string[]> }):
  Promise<{ checkoutUrl?: string; member?: boolean; requiresSignIn?: boolean; reason?: string; registered?: boolean; free?: boolean }> {
  // Same own-link attribution as the signed-in checkout (see checkoutProduct).
  let ref = '';
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('ref');
    if (fromUrl) { sessionStorage.setItem('hj_creator_ref', fromUrl); ref = fromUrl; }
    else { ref = sessionStorage.getItem('hj_creator_ref') || ''; }
  } catch { /* no window/storage */ }
  // visitorId ties the ad click that brought them to this email (guests never register).
  const visitorId = getVisitorId();
  const res = await fetch(`/api/creator/products/${productId}/guest-checkout`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, ...(visitorId ? { visitorId } : {}), ...(tier ? { tier } : {}), ...(discountCode ? { discountCode } : {}), ...(eventDate ? { eventDate } : {}), ...(ref ? { ref } : {}), ...(registerToken ? { registerToken } : {}), ...(extra || {}) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
  return data;
}

export async function checkoutProduct(productId: string, tier?: string, discountCode?: string, eventDate?: string, opts?: { useWallet?: boolean; answers?: Record<string, string | string[]>; invite?: string }): Promise<string> {
  // Creator own-community attribution: if the buyer arrived via a creator's own
  // link (?ref=<code>), pass it so the backend can apply the lower own-community
  // commission. Stashed for the session so navigating before checkout keeps it.
  // A ref matching THIS product's creator applies the lower own-community rate; a
  // ref matching a registered affiliate pays them a commission out of Hey Joe's
  // fee instead (Sep 14 2026). Anything else harmlessly falls back to standard.
  let ref = '';
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('ref');
    if (fromUrl) { sessionStorage.setItem('hj_creator_ref', fromUrl); ref = fromUrl; }
    else { ref = sessionStorage.getItem('hj_creator_ref') || ''; }
  } catch { /* no window/storage */ }
  const res = await fetch(`/api/creator/products/${productId}/checkout`, {
    method: 'POST',
    headers: headers(),
    // useWallet: apply the buyer's Hey Joe credit balance first. Covers the whole
    // charge → the server completes the purchase itself and checkoutUrl points at
    // the purchase page; covers part → Stripe for the remainder.
    body: JSON.stringify({ ...(tier ? { tier } : {}), ...(discountCode ? { discountCode } : {}), ...(eventDate ? { eventDate } : {}), ...(ref ? { ref } : {}), ...(opts?.useWallet ? { useWallet: true } : {}), ...(opts?.answers ? { answers: opts.answers } : {}), ...(opts?.invite ? { invite: opts.invite } : {}) }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Checkout failed'); }
  const data = await res.json();
  return data.checkoutUrl;
}

// ── Creator email — own-domain verification (P1) ──
export interface EmailDomainRecord { type: string; host: string; value: string; required: boolean; note?: string; }
export interface EmailDomainState { domain: string | null; status: 'none' | 'pending' | 'verified' | 'failed'; records: EmailDomainRecord[]; mailingAddress?: string; }
export async function getEmailDomain(): Promise<EmailDomainState> {
  const res = await fetch('/api/creator/email/domain', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load email domain');
  return res.json();
}
export async function addEmailDomain(domain: string): Promise<EmailDomainState> {
  const res = await fetch('/api/creator/email/domain', { method: 'POST', headers: headers(), body: JSON.stringify({ domain }) });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Failed to add domain'); }
  return res.json();
}
export async function getEmailSubscribers(): Promise<{ count: number; sample: string[] }> {
  const res = await fetch('/api/creator/email/subscribers', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load subscribers');
  return res.json();
}
export async function sendCreatorBroadcast(payload: { subject: string; body: string; mailingAddress: string }): Promise<{ broadcastId: string; total: number; queued: boolean }> {
  const res = await fetch('/api/creator/email/send', { method: 'POST', headers: headers(), body: JSON.stringify(payload) });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Send failed'); }
  return res.json();
}
export async function getBroadcastStatus(id: string): Promise<{ status: string; total: number; sent: number; failed: number }> {
  const res = await fetch(`/api/creator/email/broadcast/${id}`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load status');
  return res.json();
}
export interface EmailUsage { monthBroadcasts: number; monthEmails: number; addonActive: boolean; enforced: boolean; configured: boolean; }
export async function getEmailUsage(): Promise<EmailUsage> {
  const res = await fetch('/api/creator/email/usage', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load usage');
  return res.json();
}
export async function startEmailAddonCheckout(): Promise<string> {
  const res = await fetch('/api/creator/email/addon/checkout', { method: 'POST', headers: headers() });
  if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.error || 'Failed'); }
  const d = await res.json(); return d.checkoutUrl;
}

// Check a discount code for a product (the "Have a code?" field).
export async function validateDiscountCode(productId: string, code: string): Promise<{ valid: boolean; code?: string; amountOffCents?: number; expiresAt?: string | null }> {
  const res = await fetch(`/api/creator/products/${productId}/validate-code`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code }),
  });
  if (!res.ok) return { valid: false };
  return res.json();
}

export async function fetchMyPurchases(): Promise<ProductPurchase[]> {
  const res = await fetch('/api/my-purchases', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch purchases');
  const data = await res.json();
  return data.purchases;
}

export interface SubscriptionInfo {
  status: 'active' | 'canceled' | 'past_due';
  startedAt: string;
  currentPeriodEnd: string;
  canceledAt?: string;
}

export interface CreatorSubscription {
  _id: string;
  productId: string;
  status: 'active' | 'canceled' | 'past_due';
  currentPeriodEnd: string;
  startedAt: string;
  canceledAt?: string;
  cancelAtPeriodEnd?: boolean;
  /** A $0/month membership (a studio's free tier): no Stripe, cancel ends it right away. */
  free?: boolean;
  product?: CreatorProduct;
}

// Class-page Plus bundle claim state, echoed from the buyer's purchase doc.
export interface PlusOfferState {
  months: number;
  claimed: boolean;
  claimKind: 'new_sub' | 'existing_sub_extend' | null;
}

export async function fetchProductAccess(productId: string): Promise<{ purchased: boolean; product: CreatorProduct; subscription?: SubscriptionInfo | null; accessToken?: string; expiresIn?: number; plusOffer?: PlusOfferState; bundle?: BundleSummary | null }> {
  const res = await fetch(`/api/creator/products/${productId}/access`, { headers: getAuthHeaders() });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    const e: any = new Error(err.error || 'Access denied');
    // A non-member's 90-day replay window has closed — surface it so the viewer can
    // render the "keep it with Plus" upsell instead of a generic not-purchased state.
    if (err.replayExpired) { e.replayExpired = true; e.expiresAt = err.expiresAt || null; }
    throw e;
  }
  return res.json();
}

// Replays the member would lose if they cancel Plus (past their 90-day window, kept
// alive only by membership). Drives the cancel-save prompt. count 0 = nothing at risk.
export async function fetchReplayLibraryAtRisk(): Promise<{ count: number; titles: string[] }> {
  const res = await fetch('/api/subscription/replay-library', { headers: getAuthHeaders() });
  if (!res.ok) return { count: 0, titles: [] };
  return res.json();
}

// Featured event join link — revealed only to registered buyers (or the creator).
export async function fetchJoinLink(productId: string): Promise<{ joinUrl: string; joinProvider: string; eventDate?: string; durationMins?: number | null; questions?: RegistrationQuestion[]; answers?: RegistrationAnswers | null }> {
  const res = await fetch(`/api/creator/products/${productId}/join`, { headers: getAuthHeaders() });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Join link unavailable'); }
  return res.json();
}

// --- Sales Dashboard ---

export interface ProductStat {
  productId: string;
  title: string;
  salesCount: number;
  revenue: number;
}

export interface SaleRecord {
  _id: string;
  productTitle: string;
  buyerEmail: string;
  amountPaid: number;
  creatorShare: number;
  purchasedAt: string;
}

export interface SubscriptionMetrics {
  activeSubscribers: number;
  mrr: number;
  churn: { last30Days: number; rate: number };
  avgSubscriptionDays: number;
  ltv: number;
}

export interface MonthlyTrend {
  month: string;
  sales: number;
  revenue: number;
  newSubscribers: number;
  cancellations: number;
}

export interface SalesDashboard {
  totalSales: number;
  totalRevenue: number;
  creatorRevenue: number;
  productStats: ProductStat[];
  recentSales: SaleRecord[];
  subscriptionMetrics: SubscriptionMetrics | null;
  monthlyTrends: MonthlyTrend[];
}

export async function fetchCreatorSales(): Promise<SalesDashboard> {
  const res = await fetch('/api/creator/sales', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch sales');
  return res.json();
}

// --- Registration roster (per product) ---
export interface Registration { id?: string; cancelled?: boolean; refunded?: boolean; email: string; name: string; amountPaid: number; creatorShareCents: number; purchasedAt: string | null; eventDate?: string | null; tier?: string | null; answers?: RegistrationAnswers | null;
  /** null until the occurrence is reconciled (~30 min after the stream ends), then true/false. */
  attended?: boolean | null; attendedMinutes?: number | null; attendanceSource?: 'heyjoe_room' | 'import' | null; }
export interface AttendanceTally { occurrence: string | null; registered: number; attended: number; noShow: number; source: string; reconciledAt: string; }
export interface PlusSignup { email: string; name: string; tierName: string; status: string; subscribedAt: string | null; firstMonthCents: number; commissionCents: number; pending: boolean; viaLink?: boolean; payout?: 'paid' | 'held' | null; }
export interface Roster {
  count: number; registrations: Registration[];
  grossCents: number; creatorShareCents: number; creatorSharePercent: number;
  plusCommissionPercent: number; plusCommissionCents: number; plusSignups: PlusSignup[];
  waitlistCount?: number;
  waitlist?: { email: string; name: string; joinedAt: string | null }[];
  questions?: RegistrationQuestion[];
  earlyAccess?: { email: string; name: string; joinedAt: string | null; answers?: RegistrationAnswers | null }[];
  attendance?: AttendanceTally[];
  /** Hey Joe room or Mux stream: attendance reconciles itself. Otherwise the host pastes a list. */
  attendanceAutomatic?: boolean;
}
// Host pastes attendee emails for a room we can't see into (Zoom, a venue). Marks
// the roster attended / no-show for that occurrence; returns pasted addresses that
// matched nobody so the host can spot a typo.
export async function importAttendance(productId: string, emails: string, occurrence?: string | null): Promise<{ registered: number; attended: number; noShow: number; unmatched: string[] }> {
  const res = await fetch(`/api/creator/products/${productId}/attendance-import`, {
    method: 'POST', headers: headers(), body: JSON.stringify({ emails, occurrence: occurrence || undefined }),
  });
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || 'Could not import attendance');
  return res.json();
}

export interface EmailFlowStats { label: string; sent: number; delivered: number; opened: number; clicked: number; bounced: number; unsubscribed: number; spam: number; }
export interface EmailStats { flows: EmailFlowStats[]; recipients: Record<string, Record<string, { opened: boolean; clicked: boolean; bounced: boolean }>>; campaigns?: string[]; }
// What email did for this event: per-flow rates, and per-recipient open/click for the roster.
export async function fetchEmailStats(productId: string): Promise<EmailStats> {
  const res = await fetch(`/api/creator/products/${productId}/email-stats`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load email stats');
  return res.json();
}

export async function fetchRegistrations(productId: string): Promise<Roster> {
  const res = await fetch(`/api/creator/products/${productId}/registrations`, { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load registrations');
  return res.json();
}

// Creator cancels one registration from the builder roster. Paid seats are refunded
// in full first; the seat frees up and the buyer is emailed.
export async function cancelRegistration(productId: string, purchaseId: string): Promise<{ cancelled: boolean; refunded: boolean; refundCents: number; buyerEmail?: string }> {
  const res = await fetch(`/api/creator/products/${productId}/registrations/${purchaseId}/cancel`, { method: 'POST', headers: headers() });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || 'Failed to cancel registration');
  return data;
}

// Join the waitlist for a featured event whose seats are full (per current occurrence).
export async function joinWaitlist(productId: string): Promise<{ success: boolean; position?: number; alreadyOn?: boolean; alreadyRegistered?: boolean }> {
  const res = await fetch(`/api/creator/products/${productId}/waitlist`, { method: 'POST', headers: headers() });
  if (!res.ok) throw new Error('Failed to join waitlist');
  return res.json();
}

// Early access to a creator's upcoming seminars (shown on agency-only events for
// people who can't book this one).
// PUT a file to a presigned S3 URL with progress. fetch() cannot report upload
// progress, so a multi-hundred-megabyte video sat behind a bare spinner with no
// sign it was moving. XHR is the only way to get upload.onprogress in a browser.
// --- Products (physical goods and digital downloads) ---
// No commission; the creator pays payment processing (2.9% + 30c). A physical
// order's money is held until it's marked shipped.
export interface GoodsOption { key: string; label: string; stock: number }
export interface GoodsConfig { kind: 'physical' | 'digital'; optionName: string | null; options: GoodsOption[]; stock: number | null; shippingCents: number }
export const goodsProcessingFee = (cents: number) => Math.round(cents * 0.029) + 30;
export async function goodsCheckout(productId: string, body: { option?: string; quantity: number; email?: string }): Promise<{ checkoutUrl: string }> {
  const res = await fetch(`/api/creator/products/${productId}/goods-checkout`, { method: 'POST', headers: headers(), body: JSON.stringify(body) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not start checkout');
  return data;
}
export interface StoreOrder {
  id: string; productId: string; title: string; kind: 'physical' | 'digital'; optionName: string | null; optionLabel: string | null; quantity: number;
  buyerName: string | null; buyerEmail: string | null;
  shipping: { name: string | null; address: { line1?: string; line2?: string | null; city?: string; state?: string; postal_code?: string; country?: string } } | null;
  amountPaid: number; processingFeeCents: number | null; payoutCents: number | null;
  fulfillment: { status: 'unfulfilled' | 'shipped' | 'delivered'; shippedAt?: string; carrier?: string | null; tracking?: string | null } | null;
  payoutStatus: string | null; refunded: boolean; purchasedAt: string;
}
export async function fetchStoreOrders(): Promise<{ orders: StoreOrder[]; hasProducts: boolean }> {
  const res = await fetch('/api/creator/orders', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Could not load your orders');
  return res.json();
}
export async function markOrderShipped(purchaseId: string, carrier: string, tracking: string): Promise<{ ok: boolean; payout?: string | null }> {
  const res = await fetch(`/api/creator/orders/${purchaseId}/shipped`, { method: 'POST', headers: headers(), body: JSON.stringify({ carrier, tracking }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Could not mark it shipped');
  return data;
}

export function uploadToSignedUrl(
  uploadUrl: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      // Not every transfer reports a total; fall back to indeterminate (-1).
      if (onProgress) onProgress(e.lengthComputable && e.total > 0 ? Math.round((e.loaded / e.total) * 100) : -1);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed (${xhr.status})`)));
    xhr.onerror = () => reject(new Error('Upload failed — check your connection'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));
    xhr.send(file);
  });
}

export interface HostCheck { email: string; hasAccount: boolean | null; userType: string | null }
// Whether each configured presenter has a Hey Joe account. Casting identifies a
// viewer by the account they are signed in to, so no account means audience.
export async function fetchHostCheck(productId: string): Promise<HostCheck[]> {
  const res = await fetch(`/api/creator/products/${productId}/host-check`, { headers: getAuthHeaders() });
  if (!res.ok) return [];
  const data = await res.json().catch(() => ({}));
  return Array.isArray(data.hosts) ? data.hosts : [];
}

export async function requestEarlyAccess(productId: string, answers?: RegistrationAnswers): Promise<{ success: boolean; alreadyOn?: boolean }> {
  const res = await fetch(`/api/creator/products/${productId}/early-access`, { method: 'POST', headers: headers(), body: JSON.stringify({ answers: answers || {} }) });
  if (!res.ok) throw new Error('Failed to add you to the list');
  return res.json();
}

// --- Homepage hero (admin-managed slides; see docs/PLAN_homepage_hero_admin.md) ---
export interface HeroCtaDef { label: string; to: string }
export interface HeroSlideDef {
  id: string; enabled: boolean; kind: 'product' | 'static';
  staticKey?: 'brand' | 'audition' | 'creator';
  productId?: string; kickerPrefix?: string; kickerColor?: 'yellow' | 'pink' | 'ink';
  headlineTop?: string; headlineMarker?: string; markerFirst?: boolean; subhead?: string;
  primaryCta?: HeroCtaDef | null; secondaryCta?: HeroCtaDef | null; hideAfterEvent?: boolean;
  tileStyle?: 'card' | 'full';
  product?: CreatorProduct | null; creator?: (PublicProductCreator & { hidden?: boolean }) | null; warning?: string;
}
let _heroPromise: Promise<{ slides: HeroSlideDef[]; fromDefault?: boolean }> | null = null;
export function fetchHomepageHero(): Promise<{ slides: HeroSlideDef[]; fromDefault?: boolean }> {
  if (!_heroPromise) {
    _heroPromise = fetch('/api/homepage/hero').then(async (r) => {
      if (!r.ok) throw new Error('hero unavailable');
      return r.json();
    }).catch((e) => { _heroPromise = null; throw e; });
  }
  return _heroPromise;
}
export async function fetchAdminHero(): Promise<{ slides: HeroSlideDef[]; fromDefault: boolean; updatedAt: string | null; updatedBy: string | null }> {
  const res = await fetch('/api/admin/homepage/hero', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to load hero');
  return res.json();
}
export async function saveAdminHero(slides: HeroSlideDef[]): Promise<{ ok: boolean; slides: HeroSlideDef[] }> {
  const res = await fetch('/api/admin/homepage/hero', { method: 'PUT', headers: headers(), body: JSON.stringify({ slides }) });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Failed to save');
  return data;
}

// --- Stripe Connect ---

export interface PayoutSchedule {
  interval: 'daily' | 'weekly' | 'monthly' | 'manual';
  weekly_anchor?: string;
  monthly_anchor?: number;
  delay_days?: number;
}

export interface ConnectStatus {
  connected: boolean;
  onboarded: boolean;
  accountId?: string;
  chargesEnabled?: boolean;
  payoutsEnabled?: boolean;
  payoutSchedule?: PayoutSchedule | null;
  country?: string;
}

export async function startConnectOnboarding(country?: string): Promise<string> {
  const res = await fetch('/api/creator/connect/onboard', { method: 'POST', headers: headers(), body: JSON.stringify({ country }) });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Onboarding failed'); }
  const data = await res.json();
  return data.url;
}

export async function fetchConnectStatus(): Promise<ConnectStatus> {
  const res = await fetch('/api/creator/connect/status', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to check status');
  return res.json();
}

// How often Stripe pays out the balance. Express accounts are platform-controlled,
// so this is the only place a creator can change it.
export async function setPayoutSchedule(
  interval: 'daily' | 'weekly' | 'monthly',
  opts: { weeklyAnchor?: string; monthlyAnchor?: number } = {},
): Promise<PayoutSchedule | null> {
  const res = await fetch('/api/creator/connect/payout-schedule', {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ interval, ...opts }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to update payouts'); }
  const data = await res.json();
  return data.payoutSchedule ?? null;
}

export async function getConnectDashboardUrl(): Promise<string> {
  const res = await fetch('/api/creator/connect/dashboard', { headers: getAuthHeaders() });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed'); }
  const data = await res.json();
  return data.url;
}

// --- Marketplace ---

export interface MarketplaceCreator {
  slug: string;
  displayName: string;
  bio: string;
  imageUrl: string;
  bannerImage?: string;
  productCount: number;
  priceRange: { min: number; max: number };
  totalMembers?: number;
  hasClasses?: boolean;       // store carries dated (non-webinar) classes
  hasWebinars?: boolean;      // store carries live featured events
  hasSeries?: boolean;        // store carries on-demand courses/series
  productTypes?: string[];    // every distinct format the store carries
  nextEventDate?: string | null; // ISO of the soonest upcoming class/webinar
  freeWithPlus?: boolean;     // something here costs Plus members $0
  activeReplay?: { productId: string; title: string; until: string } | null; // a replay whose host answers questions within 24 hours right now
}

// --- Product Reviews ---

export interface ProductReview {
  _id: string;
  rating: number;
  comment: string;
  buyerEmail: string;
  createdAt: string;
}

export interface ReviewSummary {
  reviews: ProductReview[];
  averageRating: number;
  totalReviews: number;
}

export async function fetchProductReviews(productId: string): Promise<ReviewSummary> {
  const res = await fetch(`/api/creator/products/${productId}/reviews`);
  if (!res.ok) throw new Error('Failed to fetch reviews');
  return res.json();
}

// Replay Q&A: viewers ask the creator under the player; answers show inline.
export interface ReplayQuestion {
  _id: string;
  askerFirst: string;
  isMine: boolean;
  question: string;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
}

export interface ReplayQuestionList {
  viewerIsCreator: boolean;
  /** Can answer and remove questions: the creator, a co-owner, or an admin. An admin who isn't the creator can also ask. */
  viewerCanAnswer?: boolean;
  creatorFirst: string;
  /** Set when the event promises answers within 24 hours; `live` while that window is open. */
  activeReplay?: { days: number; until: string; live: boolean; upcoming: boolean } | null;
  questions: ReplayQuestion[];
}

const replayQaFail = async (res: Response, fallback: string) => {
  const err = await res.json().catch(() => ({}));
  throw new Error(err.error || fallback);
};

export async function fetchReplayQuestions(productId: string): Promise<ReplayQuestionList> {
  const res = await fetch(`/api/products/${productId}/questions`, { headers: getAuthHeaders() });
  if (!res.ok) await replayQaFail(res, 'Could not load questions');
  return res.json();
}

/** Public summary for the class page: promise, counts, and the host's answers (askers anonymous). */
export interface ReplayQuestionSummary {
  creatorFirst: string;
  activeReplay?: { days: number; until: string; live: boolean; upcoming: boolean } | null;
  total: number;
  answered: number;
  answers: { _id: string; question: string; answer: string; answeredAt: string | null }[];
}

export async function fetchReplayQuestionSummary(productId: string): Promise<ReplayQuestionSummary> {
  const res = await fetch(`/api/products/${productId}/questions/summary`);
  if (!res.ok) await replayQaFail(res, 'Could not load questions');
  return res.json();
}

export async function askReplayQuestion(productId: string, question: string): Promise<ReplayQuestion> {
  const res = await fetch(`/api/products/${productId}/questions`, { method: 'POST', headers: headers(), body: JSON.stringify({ question }) });
  if (!res.ok) await replayQaFail(res, 'Could not post that question');
  return (await res.json()).question;
}

export async function answerReplayQuestion(productId: string, qid: string, answer: string): Promise<{ answer: string; answeredAt: string }> {
  const res = await fetch(`/api/products/${productId}/questions/${qid}/answer`, { method: 'POST', headers: headers(), body: JSON.stringify({ answer }) });
  if (!res.ok) await replayQaFail(res, 'Could not save that answer');
  return res.json();
}

export async function hideReplayQuestion(productId: string, qid: string): Promise<void> {
  const res = await fetch(`/api/products/${productId}/questions/${qid}/hide`, { method: 'POST', headers: headers() });
  if (!res.ok) await replayQaFail(res, 'Could not remove that question');
}

export async function submitProductReview(productId: string, rating: number, comment: string): Promise<void> {
  const res = await fetch(`/api/creator/products/${productId}/reviews`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ rating, comment }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to submit review'); }
}

// --- Content Progress ---

export interface ContentProgress {
  s3Key: string;
  completed: boolean;
  completedAt?: string;
}

export interface ProductProgress {
  progress: ContentProgress[];
  completionPercent: number;
}

export async function fetchProductProgress(productId: string): Promise<ProductProgress> {
  const res = await fetch(`/api/creator/products/${productId}/progress`, { headers: getAuthHeaders() });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to fetch progress'); }
  return res.json();
}

export async function markContentComplete(productId: string, s3Key: string, completed: boolean): Promise<void> {
  const res = await fetch(`/api/creator/products/${productId}/progress`, {
    method: 'POST',
    headers: headers(),
    body: JSON.stringify({ s3Key, completed }),
  });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Failed to update progress'); }
}

export interface MarketplaceResponse {
  creators: MarketplaceCreator[];
  total: number;
  page: number;
  totalPages: number;
}

export interface MarketplaceParams {
  search?: string;
  category?: string; // format filter: classes | featured events | single | series
  topic?: string;    // subject filter: curated category slug from /api/marketplace/taxonomy
  page?: number;
  limit?: number;
  preview?: string;
}

export async function fetchMySubscriptions(): Promise<CreatorSubscription[]> {
  const res = await fetch('/api/my-subscriptions', { headers: getAuthHeaders() });
  if (!res.ok) throw new Error('Failed to fetch subscriptions');
  const data = await res.json();
  return data.subscriptions;
}

export async function cancelSubscription(subscriptionId: string): Promise<void> {
  const res = await fetch(`/api/creator/subscriptions/${subscriptionId}/cancel`, { method: 'POST', headers: headers() });
  if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.error || 'Cancel failed'); }
}

export async function fetchMarketplace(params: MarketplaceParams = {}): Promise<MarketplaceResponse> {
  const query = new URLSearchParams();
  if (params.search) query.set('search', params.search);
  if (params.category) query.set('category', params.category);
  if (params.topic) query.set('topic', params.topic);
  if (params.page) query.set('page', String(params.page));
  if (params.limit) query.set('limit', String(params.limit));
  if (params.preview) query.set('preview', params.preview);

  const qs = query.toString();
  const url = `/api/marketplace${qs ? `?${qs}` : ''}`;

  const res = await fetch(url);
  if (!res.ok) throw new Error('Failed to fetch marketplace');
  return res.json();
}

// --- Mux marketplace ingest ---

export interface MuxIngestResponse {
  muxAssetId: string;
  muxPlaybackId: string | null;
  muxIngestStatus: MuxIngestStatus;
  idempotent?: boolean;
}

// POST /api/mux/ingest — kick a Mux asset for a video already uploaded to S3.
// productId optional: omit for upload-time ("source-only") ingest before the
// lesson is saved onto a product — carry the returned fields on the lesson and
// they persist with the next save. Returns null if Mux is disabled (403) or
// not configured (503). Otherwise throws so the caller can surface errors.
export async function requestMuxIngest(
  body: { productId?: string; videoIndex?: number; s3Key: string; kind: "paid" | "preview" }
): Promise<MuxIngestResponse | null> {
  const res = await fetch("/api/mux/ingest", {
    method: "POST",
    headers: headers(),
    body: JSON.stringify(body),
  });
  if (res.status === 403 || res.status === 503) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || "Mux ingest failed");
  }
  return res.json();
}

export interface MuxPlaybackTokens {
  playbackId: string;
  tokens: {
    playback: string | null;
    thumbnail: string | null;
    storyboard: string | null;
  };
  expiresAt: string;
  entitled: boolean;
}

// POST /api/mux/sync/:productId — poll Mux for current asset status on every
// video / preview for the product and write the latest state back to the DB.
// Useful when the webhook isn't configured or is delayed and the editor is
// stuck on "processing". Idempotent.
export interface MuxSyncResult {
  checked: number;
  updated: number;
  results: Array<{
    assetId: string;
    location: 'paid' | 'preview';
    status?: 'preparing' | 'ready' | 'errored';
    errors?: string[];
    error?: string;
  }>;
}

export async function syncMuxStatus(productId: string): Promise<MuxSyncResult | null> {
  const res = await fetch(`/api/mux/sync/${productId}`, { method: 'POST', headers: headers() });
  if (res.status === 503) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Mux sync failed');
  }
  return res.json();
}

// GET /api/mux/playback-token/:productId/:videoIdx — mint per-lesson JWTs.
// Returns null on 503 (Mux unconfigured) so callers fall back to S3 silently.
// Throws on real errors so the caller can decide whether to retry.
export async function fetchMuxPlaybackToken(productId: string, videoIdx: number): Promise<MuxPlaybackTokens | null> {
  const res = await fetch(`/api/mux/playback-token/${productId}/${videoIdx}`, {
    headers: getAuthHeaders(),
    credentials: 'include',
  });
  if (res.status === 503) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error || 'Failed to fetch Mux playback token');
  }
  return res.json();
}

